import { MOCK_OPERATOR } from './marketingOpsPaths';

import type {
  WatchAiChat,
  WatchAiTaskKind,
  WatchCompany,
  WatchIssueChatMessage,
  WatchTowerMockState,
} from './types';

export function nextAiChatId(items: { id: number }[]): number {
  return items.reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

export function messagesForAiChat(
  state: WatchTowerMockState,
  chatId: number,
): WatchIssueChatMessage[] {
  return state.createChatMessages
    .filter((item) => item.chatId === chatId)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export function appendAiChatUserMessage(
  state: WatchTowerMockState,
  chatId: number,
  contentText: string,
  now = new Date(),
): WatchTowerMockState {
  const text = contentText.trim();
  if (!text || !state.createChats.some((item) => item.id === chatId)) {
    return state;
  }
  return {
    ...state,
    createChatMessages: [...state.createChatMessages, {
      id: nextAiChatId(state.createChatMessages),
      chatId,
      role: 'user',
      contentText: text,
      attachedContextJson: null,
      extractedJson: null,
      createdAt: now.toISOString(),
    }],
  };
}

export function appendAiChatAssistantTurn(
  state: WatchTowerMockState,
  chatId: number,
  contentText: string,
  extractedJson: Record<string, unknown> | null,
  now: Date,
  attachedContextJson: Record<string, unknown> | null = null,
): WatchTowerMockState {
  return {
    ...state,
    createChatMessages: [...state.createChatMessages, {
      id: nextAiChatId(state.createChatMessages),
      chatId,
      role: 'assistant',
      contentText,
      attachedContextJson,
      extractedJson,
      createdAt: now.toISOString(),
    }],
  };
}

function addWatchAiChat(
  state: WatchTowerMockState,
  input: {
    title: string,
    taskKind: WatchAiTaskKind,
    watchCompanyId: number | null,
  },
): { state: WatchTowerMockState, chatId: number } {
  const chatId = nextAiChatId(state.createChats);
  const chat: WatchAiChat = {
    id: chatId,
    title: input.title,
    taskKind: input.taskKind,
    watchCompanyId: input.watchCompanyId,
    agentModelId: null,
    createdBy: MOCK_OPERATOR,
    committedAt: null,
    pendingSeeds: [],
    dossierSearchKind: null,
    aiChatId: null,
  };
  return {
    chatId,
    state: {
      ...state,
      createChats: [...state.createChats, chat],
    },
  };
}

export function chatsForWatchCompany(
  state: WatchTowerMockState,
  watchCompanyId: number,
): WatchAiChat[] {
  return state.createChats.filter((chat) => chat.watchCompanyId === watchCompanyId);
}

export function openOrCreateCompanyDossierChat(
  state: WatchTowerMockState,
  company: WatchCompany,
): { state: WatchTowerMockState, chatId: number } {
  const linked = chatsForWatchCompany(state, company.id);
  if (linked.length) {
    return { state, chatId: linked[linked.length - 1].id };
  }
  return addWatchAiChat(state, {
    title: `Talk — ${company.name}`,
    taskKind: 'company_dossier',
    watchCompanyId: company.id,
  });
}

export function buildCompanyDossierContext(
  state: WatchTowerMockState,
  company: WatchCompany,
): Record<string, unknown> {
  const findings = state.findings
    .filter((item) => item.watchCompanyId === company.id)
    .map((item) => ({
      publicId: item.publicId,
      title: item.title,
      severity: item.severity,
      observedAt: item.observedAt,
      findingType: item.findingType,
    }));
  return {
    taskKind: 'company_dossier',
    company: {
      name: company.name,
      uid: company.uid,
      addressCountry: company.addressCountry,
      addressCity: company.addressCity,
      naceSection: company.naceSection,
      identifiers: company.identifiers,
    },
    products: company.products.map((item) => ({
      name: item.name,
      code: item.code,
      hsCode: item.hsCode,
    })),
    buildings: company.buildings.map((item) => ({
      name: item.name,
      addressCountry: item.addressCountry,
      addressCity: item.addressCity,
    })),
    findings,
  };
}

export type CompanyDossierSendResult =
  | { ok: true, state: WatchTowerMockState }
  | { ok: false, error: 'missing_chat' | 'missing_company', state: WatchTowerMockState };

export function sendCompanyDossierToAi(
  state: WatchTowerMockState,
  input: {
    chatId: number,
    companyId: number,
    now?: Date,
  },
): CompanyDossierSendResult {
  const now = input.now || new Date();
  const chat = state.createChats.find((item) => item.id === input.chatId);
  if (!chat) {
    return { ok: false, error: 'missing_chat', state };
  }
  const company = state.watchCompanies.find((item) => item.id === input.companyId);
  if (!company) {
    return { ok: false, error: 'missing_company', state };
  }
  const context = buildCompanyDossierContext(state, company);
  const findings = Array.isArray(context.findings) ? context.findings as { title: string, severity: string }[] : [];
  const findingLine = findings.length
    ? findings.map((item) => `${item.severity}: ${item.title}`).join('. ')
    : 'No monitoring findings in the mock store.';
  const text = [
    `${company.name} (${company.addressCountry || '—'}).`,
    `UID ${company.uid}.`,
    findingLine,
    'Canned dossier reply. The model used company facts and agent findings. No import.',
  ].join(' ');
  return {
    ok: true,
    state: appendAiChatAssistantTurn(state, input.chatId, text, null, now, context),
  };
}
